"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { TOKEN_FETCH_CONFIG, VOICE_ERROR_MESSAGES } from "@/lib/voiceConstants";
import { STREAMING_SPEECH_MODEL } from "@/lib/assembly/universalStreamingConfig";

const SimpleVoiceInputContext = createContext({});

function summarizeTranscriptForLog(text = "", maxLength = 120) {
  if (typeof text !== "string") return "";
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength)}...`;
}

function isSimpleVoiceDebugEnabled() {
  if (process.env.NODE_ENV === "development") return true;
  if (typeof window === "undefined") return false;

  try {
    return (
      window.localStorage?.getItem("salesably:voice-debug") === "1" ||
      window.sessionStorage?.getItem("salesably:voice-debug") === "1"
    );
  } catch {
    return false;
  }
}

function logSimpleVoiceDebug(...args) {
  if (!isSimpleVoiceDebugEnabled()) return;
  console.debug(...args);
}

/**
 * SimpleVoiceInput - A minimal AssemblyAI context for single-response voice recording
 *
 * This is designed for simple use cases like voice comments and FAQ answers
 * where we don't need turn management or conversation features.
 *
 * Provides compatibility with the old Assembly.jsx API while using AssemblyAI v3.
 */
const SimpleVoiceInputProvider = ({
  children,
  language = "en",
  enableSpeakerLabels = false,
}) => {
  // Core state - minimal, matching old API
  const [connecting, setConnecting] = useState(false);
  const [connectionReady, setConnectionReady] = useState(false);
  const [transcript, setTranscript] = useState(""); // Final transcript
  const [partialTranscript, setPartialTranscript] = useState(""); // Live transcript
  const [error, setError] = useState(null);
  const [tokenCircuitOpen, setTokenCircuitOpen] = useState(false);

  // WebSocket ref
  const realtimeTranscriberRef = useRef(null);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const connectionModeRef = useRef("default");

  // Session ID to distinguish real retries from StrictMode double-mounts
  const sessionIdRef = useRef(0);

  // AbortController for in-flight token fetches
  const tokenAbortControllerRef = useRef(null);

  // 1008 reconnect tracking
  const tokenReconnectAttemptsRef = useRef(0);
  const maxTokenReconnectAttempts = 3;
  const reconnectTimeoutRef = useRef(null);

  // Token fetch retry state - circuit breaker after max consecutive failures
  const tokenFetchAttemptsRef = useRef(0);
  const lastTokenFetchAttemptRef = useRef(0);
  const turnMessageCountRef = useRef(0);
  const sentAudioChunkCountRef = useRef(0);

  // Get token from API with exponential backoff and circuit breaker
  const getToken = async () => {
    try {
      const now = Date.now();

      // Circuit breaker: stop retrying after max attempts (check before backoff to fail fast)
      if (tokenFetchAttemptsRef.current >= TOKEN_FETCH_CONFIG.maxAttempts) {
        if (isMountedRef.current) {
          setTokenCircuitOpen(true);
          setError(VOICE_ERROR_MESSAGES.CIRCUIT_OPEN);
        }
        throw new Error(VOICE_ERROR_MESSAGES.CIRCUIT_OPEN);
      }

      // Create abort controller early so disconnect() can cancel backoff sleep
      if (tokenAbortControllerRef.current) {
        tokenAbortControllerRef.current.abort();
      }
      tokenAbortControllerRef.current = new AbortController();
      const { signal } = tokenAbortControllerRef.current;

      // Enforce exponential backoff for token fetch attempts
      const timeSinceLastAttempt = now - lastTokenFetchAttemptRef.current;
      const backoffDelay = Math.min(
        TOKEN_FETCH_CONFIG.baseDelayMs * Math.pow(TOKEN_FETCH_CONFIG.backoffFactor, tokenFetchAttemptsRef.current),
        TOKEN_FETCH_CONFIG.maxBackoffMs
      );

      if (
        timeSinceLastAttempt < backoffDelay &&
        tokenFetchAttemptsRef.current > 0
      ) {
        const waitTime = backoffDelay - timeSinceLastAttempt;
        logSimpleVoiceDebug(
          `[SimpleVoiceInput] Token fetch backoff: waiting ${Math.round(waitTime / 1000)}s before retry`
        );
        await new Promise((resolve, reject) => {
          if (signal.aborted) { reject(signal.reason ?? new DOMException("Aborted", "AbortError")); return; }
          const timer = setTimeout(resolve, waitTime);
          signal.addEventListener("abort", () => { clearTimeout(timer); reject(signal.reason ?? new DOMException("Aborted", "AbortError")); }, { once: true });
        });
      }

      lastTokenFetchAttemptRef.current = Date.now();

      const response = await fetch("/api/voice-agent/token", {
        method: "POST",
        signal,
      });
      const data = await response.json();

      if (data.error) {
        throw new Error(
          data.error || VOICE_ERROR_MESSAGES.TOKEN_FETCH_FAILED
        );
      }

      // Reset retry counter on success
      tokenFetchAttemptsRef.current = 0;
      lastTokenFetchAttemptRef.current = 0;

      return data.token;
    } catch (error) {
      // Don't count aborted requests toward the circuit breaker
      if (error.name !== "AbortError") {
        tokenFetchAttemptsRef.current++;
      }
      console.error(
        `[SimpleVoiceInput] Token fetch failed (attempt ${tokenFetchAttemptsRef.current}/${TOKEN_FETCH_CONFIG.maxAttempts}):`,
        error.message
      );
      throw error;
    }
  };

  // Connect to AssemblyAI WebSocket
  const connect = useCallback(async (options = {}) => {
    const requestedMode = options?.mode === "manual" ? "manual" : "default";

    logSimpleVoiceDebug("[SimpleVoiceInput] connect requested", {
      requestedMode,
      language,
      enableSpeakerLabels,
    });

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      return;
    }

    // If we already have a websocket, decide whether it's usable.
    if (realtimeTranscriberRef.current) {
      const ws = realtimeTranscriberRef.current;
      const state = ws.readyState;

      // If the existing socket is OPEN but for a different mode, recreate it.
      if (state === WebSocket.OPEN && connectionModeRef.current !== requestedMode) {
        try {
          ws.close();
        } catch (e) {
          // ignore
        }
        realtimeTranscriberRef.current = null;
        if (isMountedRef.current) {
          setConnectionReady(false);
        }
      }

      // 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
      if (state === WebSocket.OPEN) {
        if (isMountedRef.current) {
          setConnectionReady(true);
          setError(null);
        }
        return;
      }

      if (state === WebSocket.CONNECTING) {
        return;
      }

      // CLOSING/CLOSED: tear down and create a new socket.
      try {
        ws.close();
      } catch (e) {
        // ignore
      }
      realtimeTranscriberRef.current = null;
      if (isMountedRef.current) {
        setConnectionReady(false);
      }
    }

    isConnectingRef.current = true;
    const currentSession = ++sessionIdRef.current;
    if (isMountedRef.current) {
      setConnecting(true);
      setError(null);
    }

    // Persist the mode for message handling.
    connectionModeRef.current = requestedMode;

    try {
      const token = await getToken();

      // Bail out if unmounted or a newer session started (StrictMode double-mount)
      if (!isMountedRef.current || currentSession !== sessionIdRef.current) {
        isConnectingRef.current = false;
        return;
      }

      // Build parameters for Universal-3.5 Pro Streaming (v3). Name the model
      // explicitly rather than lean on the API default. Turn detection on it is
      // controlled by min_turn_silence / max_turn_silence (the older
      // end_of_turn_confidence_threshold / min_end_of_turn_silence_when_confident /
      // format_turns params have no effect on this model — formatting is always on).
      const workingParams = {
        token: token,
        sample_rate: "16000",
        speech_model: STREAMING_SPEECH_MODEL,
        ...(requestedMode === "manual"
          ? {
              // Form dictation: rely on explicit mic on/off and ForceEndpoint on
              // stop, so push max_turn_silence high to avoid auto-ending
              // mid-thought. Keep min_turn_silence moderate so spoken numbers,
              // emails, and identifiers aren't split across partials.
              min_turn_silence: "200",
              max_turn_silence: "10000",
            }
          : {
              min_turn_silence: "200",
              max_turn_silence: "3000",
            }),
      };

      // Add speaker labels if requested (matches Assembly.new.jsx).
      if (enableSpeakerLabels) {
        workingParams.speaker_labels = "true";
      }

      const params = new URLSearchParams(workingParams);
      const wsUrl = `wss://streaming.assemblyai.com/v3/ws?${params.toString()}`;

      realtimeTranscriberRef.current = new WebSocket(wsUrl);

      // Handle Turn messages - simplified for single response
      const handleTurnMessage = (data) => {
        if (data.transcript) {
          // Update partial transcript for live feedback
          if (isMountedRef.current) {
            if (connectionModeRef.current === "manual") {
              // Build a more responsive preview by including the last non-final word
              // (Universal-Streaming transcripts are immutable; the last word can lag).
              let preview = data.transcript;

              const words = Array.isArray(data.words) ? data.words : [];
              let lastNonFinal = null;
              for (let i = words.length - 1; i >= 0; i--) {
                if (words[i] && words[i].word_is_final === false) {
                  lastNonFinal = words[i].text;
                  break;
                }
              }

              const lastNonFinalText =
                typeof lastNonFinal === "string" ? lastNonFinal.trim() : "";
              if (lastNonFinalText) {
                preview = preview
                  ? `${preview} ${lastNonFinalText}`
                  : lastNonFinalText;
              }

              // When present, `utterance` is a fast finalized unit; prefer it if it is longer.
              if (data.utterance && data.utterance.length > (preview || "").length) {
                preview = data.utterance;
              }

              setPartialTranscript(preview);
            } else {
              setPartialTranscript(data.transcript);
            }
            // Transcript updates are very frequent, commenting out to reduce noise
            // console.log(
            //   `[SimpleVoiceInput] Transcript update: "${data.transcript}" (end_of_turn: ${data.end_of_turn})`
            // );
          }

          // If turn has ended, this is our final transcript
          if (data.end_of_turn) {
            if (isMountedRef.current) {
              setTranscript(data.transcript);
              // Clear partial when we have final
              setPartialTranscript("");
            }

            logSimpleVoiceDebug("[SimpleVoiceInput] Final transcript received", {
              mode: connectionModeRef.current,
              length: data.transcript?.length || 0,
              transcript: summarizeTranscriptForLog(data.transcript),
              utterance: summarizeTranscriptForLog(data.utterance),
            });
          }
        }
      };

      // Set up message event handler
      realtimeTranscriberRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case "Begin":
            logSimpleVoiceDebug("[SimpleVoiceInput] Received Begin", {
              id: message.id,
              expires_at: message.expires_at,
            });
            break;

          case "Turn":
            turnMessageCountRef.current += 1;
            logSimpleVoiceDebug("[SimpleVoiceInput] Received Turn", {
              turnCount: turnMessageCountRef.current,
              endOfTurn: Boolean(message.end_of_turn),
              transcriptLength: message.transcript?.length || 0,
              transcript: summarizeTranscriptForLog(message.transcript),
              utterance: summarizeTranscriptForLog(message.utterance),
              wordCount: Array.isArray(message.words) ? message.words.length : 0,
            });
            handleTurnMessage(message);
            break;

          case "Termination":
            logSimpleVoiceDebug("[SimpleVoiceInput] Received Termination", {
              audio_duration_seconds: message.audio_duration_seconds,
              session_duration_seconds: message.session_duration_seconds,
            });
            if (isMountedRef.current) {
              setConnectionReady(false);
            }
            break;

          default:
            if (message.error) {
              console.error("[SimpleVoiceInput] WebSocket message error", {
                type: message.type,
                error: message.error,
              });
              if (isMountedRef.current) {
                setError(message.error);
              }
            }
        }
      };

      // Set up error handler
      realtimeTranscriberRef.current.onerror = (event) => {
        console.error("[SimpleVoiceInput] WebSocket error event", event);
        if (isMountedRef.current) {
          setError(
            "Connection lost. Please check your internet connection and try again."
          );
        }
      };

      // Set up close handler
      realtimeTranscriberRef.current.onclose = (event) => {
        logSimpleVoiceDebug("[SimpleVoiceInput] WebSocket closed", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          mode: connectionModeRef.current,
        });
        if (isMountedRef.current) {
          setConnectionReady(false);
        }
        realtimeTranscriberRef.current = null;

        // Auto-reconnect on 1008 (token expired / Unauthorized)
        if (
          event.code === 1008 &&
          isMountedRef.current &&
          tokenReconnectAttemptsRef.current < maxTokenReconnectAttempts
        ) {
          tokenReconnectAttemptsRef.current++;
          logSimpleVoiceDebug(
            `[SimpleVoiceInput] Token expired (1008), reconnecting (attempt ${tokenReconnectAttemptsRef.current}/${maxTokenReconnectAttempts})`
          );
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connect({ mode: connectionModeRef.current });
            }
          }, 500);
        } else if (event.code === 1008 && isMountedRef.current) {
          console.error(
            `[SimpleVoiceInput] Max token reconnection attempts (${maxTokenReconnectAttempts}) reached, stopping retries`
          );
          if (isMountedRef.current) {
            setError("Session expired. Please reconnect.");
          }
        }
      };

      // Set up open handler
      realtimeTranscriberRef.current.onopen = () => {
        // Reset 1008 reconnect counter on successful connection
        tokenReconnectAttemptsRef.current = 0;
        turnMessageCountRef.current = 0;
        sentAudioChunkCountRef.current = 0;
        logSimpleVoiceDebug("[SimpleVoiceInput] WebSocket open", {
          mode: connectionModeRef.current,
        });
        if (isMountedRef.current) {
          setConnectionReady(true);
          setError(null);
        }
      };
    } catch (error) {
      // Don't log/set error for aborted requests or stale sessions
      if (error.name === "AbortError" || currentSession !== sessionIdRef.current) {
        realtimeTranscriberRef.current = null;
        return;
      }
      if (isMountedRef.current) {
        setConnectionReady(false);
        // Use the error message from getToken if available, otherwise use a generic message
        setError(
          error.message ||
            "Unable to connect to transcription service. Please try again."
        );
      }
      realtimeTranscriberRef.current = null;
    } finally {
      if (currentSession === sessionIdRef.current) {
        isConnectingRef.current = false;
      }
      if (isMountedRef.current && currentSession === sessionIdRef.current) {
        setConnecting(false);
      }
    }
  }, [language, enableSpeakerLabels]);

  // Disconnect from AssemblyAI
  const disconnect = useCallback(async () => {
    logSimpleVoiceDebug("[SimpleVoiceInput] disconnect requested", {
      mode: connectionModeRef.current,
      connectionReady,
    });
    // Invalidate current session so any in-flight connect() calls bail out
    sessionIdRef.current++;
    isConnectingRef.current = false;

    // Abort any pending token fetch
    if (tokenAbortControllerRef.current) {
      tokenAbortControllerRef.current.abort();
      tokenAbortControllerRef.current = null;
    }

    if (realtimeTranscriberRef.current) {
      try {
        // Close regardless of state (OPEN/CONNECTING/CLOSING) to avoid stuck sockets.
        if (realtimeTranscriberRef.current.readyState !== WebSocket.CLOSED) {
          realtimeTranscriberRef.current.close();
        }
      } catch (error) {
        console.warn("[SimpleVoiceInput] Error during disconnect:", error);
      }
      realtimeTranscriberRef.current = null;
    }

    // Clear reconnect timer
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    tokenReconnectAttemptsRef.current = 0;
    tokenFetchAttemptsRef.current = 0;
    lastTokenFetchAttemptRef.current = 0;

    // Reset state
    setConnectionReady(false);
    setConnecting(false);
    setTranscript("");
    setPartialTranscript("");
    setError(null);
    setTokenCircuitOpen(false);
  }, []);

  // Reset the circuit breaker and retry connection
  const retryConnection = useCallback(
    (options = {}) => {
      // Ignore event objects passed from direct onClick handlers
      const opts = typeof options === 'object' && options && !options.nativeEvent ? options : {};
      tokenFetchAttemptsRef.current = 0;
      lastTokenFetchAttemptRef.current = 0;
      tokenReconnectAttemptsRef.current = 0;
      setTokenCircuitOpen(false);
      setError(null);
      connect(opts);
    },
    [connect]
  );

  // Send audio to AssemblyAI
  const sendAudio = useCallback(
    async (audioBuffer) => {
      if (!realtimeTranscriberRef.current || !connectionReady) {
        return false;
      }

      // Validate audioBuffer
      if (!audioBuffer) {
        return false;
      }

      if (
        !(audioBuffer instanceof ArrayBuffer) &&
        !(audioBuffer instanceof Blob)
      ) {
        console.error(
          "[SimpleVoiceInput] Invalid audioBuffer type:",
          typeof audioBuffer
        );
        return false;
      }

      try {
        if (realtimeTranscriberRef.current.readyState === WebSocket.OPEN) {
          sentAudioChunkCountRef.current += 1;
          const chunkSize =
            audioBuffer instanceof ArrayBuffer ? audioBuffer.byteLength : audioBuffer.size;
          if (
            sentAudioChunkCountRef.current <= 5 ||
            sentAudioChunkCountRef.current % 20 === 0
          ) {
            logSimpleVoiceDebug("[SimpleVoiceInput] Sent audio chunk", {
              chunkNumber: sentAudioChunkCountRef.current,
              bytes: chunkSize,
              mode: connectionModeRef.current,
            });
          }
          realtimeTranscriberRef.current.send(audioBuffer);
          return true;
        }

        return false;
      } catch (error) {
        console.error("[SimpleVoiceInput] Error sending audio:", error.message);
        if (isMountedRef.current) {
          setError("Connection interrupted. Please try again.");
        }
        return false;
      }
    },
    [connectionReady]
  );

  // Reset transcripts
  const reset = useCallback(() => {
    setTranscript("");
    setPartialTranscript("");
  }, []);

  // Force the server to endpoint the current turn (useful for manual mic on/off mode)
  const forceEndpoint = useCallback(() => {
    const ws = realtimeTranscriberRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;

    try {
      ws.send(JSON.stringify({ type: "ForceEndpoint" }));
      return true;
    } catch (e) {
      console.warn("[SimpleVoiceInput] ForceEndpoint failed:", e);
      return false;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Invalidate current session so in-flight connect() calls bail out
      sessionIdRef.current++;
      // Abort any in-flight token fetch
      if (tokenAbortControllerRef.current) {
        tokenAbortControllerRef.current.abort();
        tokenAbortControllerRef.current = null;
      }
      // Clean up WebSocket connection
      if (realtimeTranscriberRef.current) {
        if (realtimeTranscriberRef.current.readyState === WebSocket.OPEN) {
          realtimeTranscriberRef.current.close();
        }
        realtimeTranscriberRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, []);

  // Context value - matches old Assembly.jsx API
  const contextValue = {
    connect,
    disconnect,
    sendAudio,
    connecting,
    connectionReady,
    transcript, // Final transcript (compatible with old API)
    partialTranscript, // Live transcript (compatible with old API)
    error,
    tokenCircuitOpen,
    retryConnection,
    reset,
    forceEndpoint,
    enableSpeakerLabels,
    // These are for compatibility but not used in simple voice input
    utterances: [], // Empty array for compatibility
    vadEnabled: false, // Always false for simple input
    toggleVad: () => {}, // No-op for compatibility
  };

  return (
    <SimpleVoiceInputContext.Provider value={contextValue}>
      {children}
    </SimpleVoiceInputContext.Provider>
  );
};

// Hook to use SimpleVoiceInput context
const useSimpleVoiceInput = () => {
  const context = useContext(SimpleVoiceInputContext);
  if (!context) {
    throw new Error(
      "useSimpleVoiceInput must be used within a SimpleVoiceInputProvider"
    );
  }
  return context;
};

// Export both the provider and hook
// Also export with old names for drop-in compatibility
export {
  SimpleVoiceInputProvider,
  SimpleVoiceInputProvider as AssemblyContextProvider, // Compatibility alias
  useSimpleVoiceInput,
  useSimpleVoiceInput as useAssembly, // Compatibility alias
};
